import {
  arrayOf,
  bool,
  elementType,
  func,
  number,
  oneOf,
  oneOfType,
  shape,
  string,
} from "prop-types";
import React, { forwardRef, useMemo } from "react";

import styled from "styled-components/macro";
import { themeVariants } from "theme";

import FnrSelectionDisplay from "FnrSelectionDisplay";
import FnrTypography from "FnrTypography/FnrTypography";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import { FnrPopoverOption, FnrPopoverOptionsList } from "UI/FnrPopover2";

import { useOnClickOutside } from "Utilities/Hooks/useOnClickOutside";

const SFnrButtonWrapper = styled(FnrButtonWrapper)`
  ${({ $fullWidth }) => ($fullWidth ? `width: 100%;` : "")}
  ${({ $minWidth }) => ($minWidth ? `min-width: ${$minWidth}px;` : "")}
`;

const dropdownListStylesByVariant = ({
  $variant,
  theme: {
    colors: { fnBlue, white, transparent, textLight },
    shadows: { selectList },
  },
}) => {
  switch ($variant) {
    case themeVariants.emphasized:
      return `
      color: ${textLight};
      background-color: ${transparent};
      padding: 0px;
      `;
    default:
      return `
        padding: 2px;
        color: ${fnBlue};
        background-color: ${white};
        ${selectList};
      `;
  }
};

const DropdownList = styled("ul")`
  width: max-content;
  max-width: 300px;
  min-width: 245px;
  border-radius: 8px;
  max-height: 300px;
  overflow: auto;
  margin: 0;
  ${dropdownListStylesByVariant}
`;

const variantProps = {
  color: { emphasized: "blue", default: "white" },
  textColor: { emphasized: "textLight", default: "textSecondary" },
  hoverColor: { emphasized: "ghostWhite", default: "fnGrey" },
};

const fnrSelectListCancelKey = "fnrSelectListCancelKey";

/**
 * @param {object} param0
 * @param {name} param0.name - the name appended to the id. We may want to get rid of this.
 * @param {function} param0.onSelect - the function fired when a selection is made. First arg will be the currently selected option(s)
 * @param {[SelectListOption]} param0.options - the options the will appear in the dropdown.
 * @param {SelectListOption} param0.selected - the option currently selected. Passing this in overrides local state.
 * @param {"default"|"emphasized"} param0.variant - set to emphasized to add some OOMF. TODO: make this a boolean: emphasized.
 * @param {boolean} param0.fullWidth - Allows the ButtonComponent to take up the full width.
 * @param {boolean} param0.multiselect - Allows multiple options to be selected.
 * @param {number} param0.minWidth - minimum width of container in px.
 * @param {import("react").ElementType} param0.ButtonComponent - the component used for clicking. Defaults to FnrSelectionDisplay. Takes in ...rest.
 * @param {boolean} param0.throttleProps - set to true to prevent generated props from going to ButtonComponent (selectedId, multiselect, variant, options, and active)
 * @param {function} param0.onOpenDropdown - if provided, is fired when the dropdown opens
 * @param {function} param0.onCloseDropdown - if provided, is fired when the dropdown closes
 * @returns
 */
const FnrSelectList2 = forwardRef(
  (
    {
      name,
      onSelect,
      options,
      selected: selectedArg,
      variant = "default",
      fullWidth,
      multiselect,
      minWidth,
      ButtonComponent = FnrSelectionDisplay,
      throttleProps,
      onOpenDropdown,
      onCloseDropdown,
      dataCancelClickAway = fnrSelectListCancelKey,
      ...rest
    },
    ref
  ) => {
    const dropdownRef = React.useRef();
    const localRef = React.useRef();
    const containerRef = ref || localRef;
    const [showDropdown, setShowDropdown] = React.useState(false);
    const [selected, setSelected] = React.useState();

    const selectedId = useMemo(() => {
      let localSelectedArg = selectedArg || selected;
      if (multiselect && !Array.isArray(localSelectedArg))
        localSelectedArg = localSelectedArg ? [localSelectedArg] : [];
      if (multiselect) return localSelectedArg.map(({ id }) => id);
      return localSelectedArg?.id || selected?.id;
    }, [multiselect, selectedArg, selected]);

    useOnClickOutside(
      containerRef,
      (e) => {
        if (showDropdown) {
          setShowDropdown(false);
          if (onCloseDropdown) onCloseDropdown();
        }
      },
      dataCancelClickAway
    );

    const toggleDropdown = () => {
      setShowDropdown((dropdown) => {
        if (dropdown && onCloseDropdown) onCloseDropdown();
        else if (onOpenDropdown) onOpenDropdown();
        return !dropdown;
      });
    };

    const handleSelection = (e, option) => {
      e.stopPropagation();

      if (multiselect) {
        // if the value is already selected, the deselect the value.
        const newIdArray = [...selectedId];
        if (!newIdArray.includes(option.id)) {
          newIdArray.push(option.id);
        } else {
          newIdArray.splice(newIdArray.indexOf(option.id), 1);
        }
        const newValue = newIdArray.map((currentId) =>
          options.find(({ id }) => id === currentId)
        );
        setSelected(newValue);
        if (onSelect) {
          onSelect(newValue);
        }
      } else {
        setSelected(option);
        setShowDropdown(false);

        if (onSelect) {
          onSelect(option);
        }
      }
    };

    const buttonComponentProps = useMemo(() => {
      if (throttleProps) return rest;
      return {
        selectedId,
        multiselect,
        variant,
        options,
        active: showDropdown,
        ...rest,
      };
    }, [
      multiselect,
      options,
      rest,
      selectedId,
      showDropdown,
      throttleProps,
      variant,
    ]);

    return (
      <SFnrButtonWrapper
        $minWidth={minWidth}
        ref={containerRef}
        $fullWidth={fullWidth}
        type="button"
        onClick={toggleDropdown}
      >
        <ButtonComponent
          id={`FnrSelectList2_${name}`}
          ref={dropdownRef}
          {...buttonComponentProps}
        />
        <FnrPopoverOptionsList
          show={showDropdown}
          buttonRef={dropdownRef}
          color={variantProps.color[variant]}
          background={variantProps.hoverColor[variant]}
          dataCancelClickAway={dataCancelClickAway}
        >
          <DropdownList role="listbox" $variant={variant}>
            {options.map((option) => (
              <FnrPopoverOption
                key={option.id}
                onClick={(e) => {
                  handleSelection(e, option);
                }}
                selected={
                  Array.isArray(selectedId)
                    ? selectedId.includes(option?.id)
                    : selectedId === option?.id
                }
              >
                <FnrTypography
                  color={variantProps.textColor[variant]}
                  yMarginAdjust={2}
                >
                  {option.label}
                </FnrTypography>
              </FnrPopoverOption>
            ))}
          </DropdownList>
        </FnrPopoverOptionsList>
      </SFnrButtonWrapper>
    );
  }
);

const optionShape = shape({
  id: oneOfType([string, number]).isRequired,
  label: string,
});

FnrSelectList2.propTypes = {
  name: string.isRequired,
  options: arrayOf(optionShape).isRequired,
  onSelect: func,
  selected: oneOfType([optionShape, arrayOf(optionShape)]),
  variant: oneOf(["default", "emphasized"]),
  fullWidth: bool,
  multiselect: bool,
  minWidth: number,
  throttleProps: bool,
  ButtonComponent: elementType,
  onOpenDropdown: func,
  onCloseDropdown: func,
  dataCancelClickAway: string,
};

export default FnrSelectList2;
